/**
 * Full-page overlay shown when user hits a feature they don't have access to.
 * Shows plan comparison and upgrade CTA.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlan } from '../contexts/PlanContext';
import { getPlanById, getRequiredPlanForFeature, PLANS } from '../constants/plans';
import './UpgradeOverlay.css';

const FEATURE_LABELS = {
  leaderboard: 'Leaderboard & global stats',
  tournament_manage: 'Create & manage tournaments',
  tournament_schedule: 'Tournament schedule management',
  tournament_dashboard: 'Tournament dashboard',
  match_history: 'Match history',
  host_match: 'Host a match',
  group_manage: 'Manage teams & groups',
  tournament_custom: 'Custom tournament names & images',
};

function getFeatureLabel(featureKey) {
  return FEATURE_LABELS[featureKey] || featureKey?.replace(/_/g, ' ') || 'This feature';
}

export default function UpgradeOverlay({ featureKey, className = '' }) {
  const navigate = useNavigate();
  const { planId } = usePlan();
  const requiredPlanId = featureKey ? getRequiredPlanForFeature(featureKey) : 'premium';
  const requiredPlan = getPlanById(requiredPlanId);
  const currentPlan = getPlanById(planId);
  const label = getFeatureLabel(featureKey);

  return (
    <div className={`upgrade-overlay ${className}`.trim()} role="dialog" aria-modal="true" aria-labelledby="upgrade-overlay-title">
      <div className="upgrade-overlay-backdrop" />
      <div className="upgrade-overlay-card">
        <div className="upgrade-overlay-icon" aria-hidden>🔒</div>
        <h2 id="upgrade-overlay-title" className="upgrade-overlay-title">Upgrade to unlock</h2>
        <p className="upgrade-overlay-message">
          <strong>{label}</strong> is available on the <strong>{requiredPlan.name}</strong> plan or higher.
          {planId && (
            <span> Your current plan is <strong>{currentPlan.name}</strong>.</span>
          )}
        </p>
        <div className="upgrade-overlay-plans">
          <p className="upgrade-overlay-plans-heading">Plan comparison</p>
          {PLANS.map(p => (
            <div key={p.id} className="upgrade-overlay-plan-item">
              <span className="upgrade-overlay-plan-name">
                {p.name}
                {p.tagline && <span className="upgrade-overlay-plan-tagline"> · {p.tagline}</span>}
              </span>
              <span className="upgrade-overlay-plan-desc">{p.description}</span>
            </div>
          ))}
        </div>
        <div className="upgrade-overlay-actions">
          <button
            type="button"
            className="upgrade-overlay-btn upgrade-overlay-btn-primary"
            onClick={() => navigate('/subscription')}
          >
            View plans & upgrade
          </button>
          <button
            type="button"
            className="upgrade-overlay-btn upgrade-overlay-btn-secondary"
            onClick={() => navigate(-1)}
          >
            Go back
          </button>
        </div>
      </div>
    </div>
  );
}
